import { Component, type ErrorInfo, type ReactNode } from "react";
import { getCrashLog } from "../diagnostics/crashLog.js";

interface State {
  error: Error | null;
}

/**
 * Catches uncaught render errors anywhere in the tree and renders a
 * legible fallback. Without this, a thrown error unmounts everything
 * and the user sees a blank page with nothing in the DOM — the
 * symptom we hit on the demo flow.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[radar-sim] render error:", error, info.componentStack);
    try {
      getCrashLog().record({
        source: "render",
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack ?? undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        userAgent:
          typeof window !== "undefined"
            ? window.navigator.userAgent
            : undefined,
      });
    } catch {
      // Swallow — never let the crash logger itself break recovery.
    }
  }

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div
          role="alert"
          style={{
            maxWidth: 640,
            margin: "64px auto",
            padding: 24,
            borderRadius: 12,
            background: "var(--bg-card, #1f2937)",
            border: "1px solid var(--danger, #ef4444)",
            color: "var(--text, #f3f4f6)",
            fontFamily: "var(--font-ui, system-ui, sans-serif)",
          }}
        >
          <h1 style={{ fontSize: 18, margin: "0 0 8px" }}>
            Something went wrong rendering the simulator.
          </h1>
          <p style={{ fontSize: 14, opacity: 0.85, margin: "0 0 16px" }}>
            The error is logged to the browser console. Reload the page to
            try again — if the error persists, please report it with the
            stack trace below.
          </p>
          <pre
            style={{
              fontFamily: "var(--font-mono, ui-monospace, monospace)",
              fontSize: 12,
              padding: 12,
              borderRadius: 6,
              background: "rgba(0,0,0,0.3)",
              overflow: "auto",
              maxHeight: 240,
              whiteSpace: "pre-wrap",
            }}
          >
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack ?? ""}
          </pre>
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button
              onClick={() => {
                this.setState({ error: null });
                window.location.reload();
              }}
              style={{
                background: "var(--accent, #38bdf8)",
                color: "#0b1220",
                border: "none",
                borderRadius: 6,
                padding: "8px 16px",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <a
              href="#diagnostics"
              onClick={() => this.setState({ error: null })}
              style={{
                background: "transparent",
                color: "var(--text, #f3f4f6)",
                border: "1px solid var(--border-strong, #4b5563)",
                borderRadius: 6,
                padding: "8px 16px",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              View diagnostics
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
